import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { createContext, useContext, useEffect } from "react";

/**
 * The native launch splash stays up until the first screen has something to
 * show: every loading state that would otherwise fill it holds the splash while
 * mounted, and it hides the first time nothing holds it, once per launch.
 * `SplashScreen.launchShowDuration` in capacitor.config.ts caps the wait for a
 * launch that never gets there.
 */
let holds = 0;
let released = false;

function releaseIfUnheld() {
  // After the commit: a loading state's unmount and the hold of the screen
  // replacing it run in the same effect flush.
  queueMicrotask(() => {
    if (released || holds > 0) {
      return;
    }
    released = true;
    if (Capacitor.isNativePlatform()) {
      SplashScreen.hide().catch((err) => {
        console.error("[splash] hide failed", err);
      });
    }
  });
}

/**
 * False under a screen that is mounted but not shown, whose loading states
 * must not keep the splash over the one that is.
 */
export const LaunchSplashHolds = createContext(true);

/** Keeps the launch splash up while `active` and the caller is mounted. */
export function useLaunchSplashHold(active = true) {
  const shown = useContext(LaunchSplashHolds);
  const holding = active && shown;
  useEffect(() => {
    if (!holding) {
      return;
    }
    holds += 1;
    return () => {
      holds -= 1;
      releaseIfUnheld();
    };
  }, [holding]);
}

/**
 * Called once by the root: hides the splash after the first commit unless a
 * loading state below it holds it.
 */
export function useLaunchSplashRelease() {
  useEffect(releaseIfUnheld, []);
}
